import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  loadHomologState,
  mergeHomologAutoDetections,
  saveHomologState,
  toggleHomologManualCheck,
} from "./baasHomologacao";
import { detectHomologCompletions } from "./baasHomologacaoDetect";

/**
 * Shared homolog checklist state: localStorage + auto-detect probes.
 *
 * @param {{
 *   checklistKey: string|null,
 *   instance: object|null,
 *   visibleSteps: array,
 *   enabled?: boolean,
 * }} options
 */
export const useHomologChecklist = ({
  checklistKey,
  instance,
  visibleSteps,
  enabled = true,
}) => {
  const [checks, setChecks] = useState(() =>
    checklistKey ? loadHomologState(checklistKey).checks : {},
  );
  const [sources, setSources] = useState(() =>
    checklistKey ? loadHomologState(checklistKey).sources : {},
  );
  const [detecting, setDetecting] = useState(false);
  const [lastDetectedAt, setLastDetectedAt] = useState(null);
  const runIdRef = useRef(0);

  const stepSignature = useMemo(
    () =>
      (visibleSteps || [])
        .map((step) => `${step.id}:${step.detect || ""}`)
        .join("|"),
    [visibleSteps],
  );

  const applyState = useCallback(
    (next, { persist = true } = {}) => {
      setChecks(next.checks);
      setSources(next.sources);
      if (persist && checklistKey) {
        saveHomologState(checklistKey, {
          checks: next.checks,
          sources: next.sources,
        });
      }
    },
    [checklistKey],
  );

  useEffect(() => {
    if (!checklistKey) {
      setChecks({});
      setSources({});
      return;
    }
    const loaded = loadHomologState(checklistKey);
    setChecks(loaded.checks);
    setSources(loaded.sources);
  }, [checklistKey]);

  const runDetect = useCallback(async () => {
    if (!enabled || !checklistKey || !instance) return null;
    const runId = ++runIdRef.current;
    setDetecting(true);
    try {
      const detected = await detectHomologCompletions(instance, {
        steps: visibleSteps,
      });
      if (runId !== runIdRef.current) return null;

      const current = loadHomologState(checklistKey);
      const merged = mergeHomologAutoDetections(current, detected, visibleSteps);
      if (merged.changed) {
        applyState(merged);
      } else {
        setChecks(merged.checks);
        setSources(merged.sources);
      }
      setLastDetectedAt(Date.now());
      return detected;
    } catch {
      if (runId === runIdRef.current) {
        setLastDetectedAt(Date.now());
      }
      return null;
    } finally {
      if (runId === runIdRef.current) {
        setDetecting(false);
      }
    }
  }, [enabled, checklistKey, instance, visibleSteps, applyState]);

  useEffect(() => {
    if (!enabled || !checklistKey || !instance) return undefined;
    runDetect();
    return undefined;
    // Re-probe when instance / visible steps / key change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stepSignature encodes visibleSteps
  }, [enabled, checklistKey, instance?.id, instance?.baasInstanceId, stepSignature]);

  useEffect(() => {
    if (!enabled || !checklistKey || !instance) return undefined;

    const onFocus = () => {
      runDetect();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") runDetect();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, checklistKey, instance, runDetect]);

  const toggle = useCallback(
    (id) => {
      if (!checklistKey) return;
      const next = toggleHomologManualCheck({ checks, sources }, id);
      applyState(next);
    },
    [checklistKey, checks, sources, applyState],
  );

  return {
    checks,
    sources,
    detecting,
    lastDetectedAt,
    toggle,
    refresh: runDetect,
  };
};
